import Vue from 'vue';
import Router from 'vue-router';
import store from '@/store';
import EventBus from '@/components/shared/eventbus';

// Landing screen
const LandingScreen = () => import('@/screens/landing/LandingScreen');
const PricingScreen = () => import('@/screens/landing/PricingScreen');

// Welcome screen
const WelcomeScreen = () => import('@/screens/users/WelcomeScreen');
const AccountSettingsScreen = () =>
  import('@/screens/users/AccountSettingsScreen');

// Classes screen
const ClassesScreen = () => import('@/screens/classes/ClassesScreen');
const ClassScreen = () => import('@/screens/classes/ClassScreen');
const ClassRankingScreen = () => import('@/screens/classes/ClassRankingScreen');
const StudentSummaryData = () => import('@/screens/classes/StudentSummaryData');
const ClassSummaryData = () => import('@/screens/classes/ClassSummaryData');

// Student screen
const StudentScreen = () => import('@/screens/students/StudentScreen');

// Exercises screen
const ExercisesScreen = () => import('@/screens/exercises/ExercisesScreen');

// AI lesson generator screen
const AiLessonGeneratorScreen = () =>
  import('@/screens/exercises/AiLessonGeneratorScreen');
const AiLessonListenScreen = () =>
  import('@/screens/exercises/AiLessonListenScreen');

// Assignments Screen
const AssignmentsScreen = () =>
  import('@/screens/assignments/AssignmentsScreen');

// Purchase Screen
const PurchaseScreen = () => import('@/screens/purchase/PurchaseScreen');

// Page not found
const PageNotFoundScreen = () =>
  import(
    /* webpackChunkName: "pageNotFound" */ '@/screens/shared/PageNotFound'
  );

// Not compatible page
const NotCompatibleScreen = () =>
  import(
    /* webpackChunkName: "notCompatible" */ '@/screens/shared/NotCompatible'
  );

// Unsubscribed page
const UnsubscribedScreen = () =>
  import(
    /* webpackChunkName: "unsubscribed" */ '@/screens/shared/Unsubscribed'
  );

// Privacy policy page
const PrivacyPolicy = () => import('@/screens/shared/PrivacyPolicy');

// Terms and conditions
const TermsAndConditions = () => import('@/screens/shared/TermsAndConditions');

// Coupons screen
const CouponsScreen = () => import('@/screens/users/CouponsScreen');

// Coupon details screen
const CouponScreen = () => import('@/screens/users/CouponScreen');

const DKTAuthenticationScreen = () =>
  import('@/screens/DKT/DKTAuthenticationScreen');

const DKTRedirectScreen = () => import('@/screens/DKT/DKTRedirectScreen');

const RedirectScreen = () => import('@/screens/DKT/RedirectScreen');

const AccessibilitySettingsScreen = () =>
  import('@/screens/users/AccessibilitySettingsScreen');

Vue.use(Router);

const withoutAuthentication = [
  'DKTAuthenticationScreen',
  'DKTRedirectScreen',
  'RedirectScreen',
  'ErrorPages',
  'LandingScreen',
  'WelcomeScreen',
  'PurchaseScreen',
  'Exercises'
];
const disabledWithAuthentication = [
  '/users/login',
  '/users/request-password-reset',
  '/users/registration'
];

// eslint-disable-next-line
const guard = (to, from, next) => {
  EventBus.closeGlobalToast();
  if (store.getters.hasToken) {
    if (
      disabledWithAuthentication.includes(to.path) ||
      to.name === 'PasswordResetScreen'
    ) {
      next('/classes');
      return;
    }
    next({ name: ClassesScreen });
  } else if (
    (to.name !== 'LandingScreen' &&
      !withoutAuthentication.includes(to.meta.group)) ||
    (to.meta.group === 'Exercises' && to.params.classId) ||
    to.query.lessonIds
  ) {
    store.commit('settings/SET_GUEST_LINK', to.fullPath);
    next('/users/login');
  } else if (to.meta.group === 'Exercises' && to.params.classId) {
    next('/exercises/search');
  } else if (
    to.path.includes('users/registration') &&
    ['production', 'uat'].includes(process.env.NODE_ENV)
  ) {
    next('/users/login');
  } else if (to.path === '/' || withoutAuthentication.includes(to.meta.group)) {
    next();
  } else {
    next('/');
  }
};

const router = new Router({
  // mode: process.env.NODE_ENV === 'development' ? 'hash' : 'history',
  mode: 'history',
  // eslint-disable-next-line consistent-return
  scrollBehavior(to, from, savedPosition) {
    if (
      to.path !== '/purchase/enquiry' &&
      to.path === '/purchase' &&
      from.name !== 'PurchaseScreen' &&
      to.path !== '/purchase/billing' &&
      from.path !== '/purchase' &&
      to.hash === ''
    ) {
      if (to.name !== from.name) {
        return { x: 0, y: 0 };
      }
    } else if (to.hash !== '') {
      return { selector: to.hash };
    } else {
      return new Promise((resolve, reject) => {
        setTimeout(() => {
          resolve({ x: 0, y: 0 });
        }, 400);
      });
    }
  },
  routes: [
    {
      path: '/',
      name: store.getters.hasToken ? 'ClassesScreen' : 'LandingScreen',
      component: store.getters.hasToken ? ClassesScreen : LandingScreen,
      meta: {
        group: 'LandingScreen'
      }
    },
    {
      path: '/pricing',
      name: 'PricingScreen',
      component: PricingScreen,
      meta: {
        group: 'LandingScreen',
        options: {
          needHeader: false
        }
      }
    },
    {
      path: '/users/registration',
      name: 'RegistrationScreen',
      component: WelcomeScreen,
      alias: [
        '/users/login',
        '/users/request-password-reset',
        '/users/registration/role'
      ],
      meta: {
        group: 'WelcomeScreen'
      }
    },
    {
      path: '/users/reset-password/:token?',
      name: 'PasswordResetScreen',
      component: WelcomeScreen,
      meta: {
        group: 'WelcomeScreen'
      }
    },
    {
      path: '/users/account-settings',
      name: 'AccountSettingsScreen',
      component: AccountSettingsScreen,
      meta: {
        group: 'Account',
        pageTitleKey: 'app.accountSettings.pageTitle'
      }
    },
    {
      path: '/classes',
      name: 'ClassesScreen',
      component: ClassesScreen,
      meta: {
        group: 'Classes',
        pageTitleKey: 'app.classes.pageTitle'
      }
    },
    {
      path: '/classes/:id/overview',
      name: 'ClassScreen',
      component: ClassScreen,
      alias: [
        '/classes/:id/assignments',
        '/classes/:id/students',
        '/classes/:id/statistics',
        '/classes/:id/settings',
        '/classes/:id/syllabus'
      ],
      meta: {
        group: 'Class',
        pageClass: 'has-userimagegroup',
        pageTitleKey: 'app.classes.pageTitle',
        isDisabledForSubstitute: true
      }
    },
    {
      path: '/classes/:id/ranking',
      name: 'ClassRankingScreen',
      component: ClassRankingScreen,
      meta: {
        group: 'Class',
        pageClass: 'has-userimagegroup',
        pageTitleKey: 'app.classes.pageTitle',
        isDisabledForSubstitute: true
      }
    },
    {
      path: '/classes/:id/overview/students/:studentId/overview',
      name: 'StudentScreen',
      component: StudentScreen,
      alias: [
        '/classes/:id/overview/students/:studentId/assignments',
        '/classes/:id/overview/students/:studentId/activity',
        '/classes/:id/overview/students/:studentId/statistics'
      ],
      meta: {
        group: 'Students',
        isDisabledForSubstitute: true
      }
    },
    {
      path: '/exercises/:classId?/search',
      name: 'ExercisesScreen',
      component: ExercisesScreen,
      alias: [
        '/exercises/:classId?/curriculum',
        '/exercises/:classId?/learn',
        '/exercises/:classId?/speaking',
        '/exercises/:classId?/grammar',
        '/exercises/:classId?/saved'
      ],
      meta: {
        group: 'Exercises'
      }
    },
    {
      path: '/exercises/:classId?/ai-lesson',
      name: 'AiLessonGeneratorScreen',
      component: AiLessonGeneratorScreen,
      meta: {
        group: 'Exercises',
        // `headerBtnText` lives on the persistent LoggedInLayout: ClassScreen and
        // StudentScreen set it in `created()` and nobody clears it, so arriving here
        // from either one (e.g. the class dashboard's AI lesson banner) dragged their
        // "New assignment" button into the wizard header — where it is both wrong and
        // dead (`headerBtnClicked` has no case for this route).
        hideHeaderBtn: true
      }
    },
    {
      path: '/exercises/:classId?/search/student/:studentId',
      name: 'ExercisesScreen',
      component: ExercisesScreen,
      alias: [
        '/exercises/:classId?/curriculum/student/:studentId',
        '/exercises/:classId?/weekly/student/:studentId',
        '/exercises/:classId?/learn/student/:studentId',
        '/exercises/:classId?/speaking/student/:studentId',
        '/exercises/:classId?/grammar/student/:studentId',
        '/exercises/:classId?/saved/student/:studentId'
      ],
      meta: {
        group: 'Exercises'
      }
    },
    {
      path: '/assignments/:assignmentId',
      name: 'AssignmentsScreen',
      component: AssignmentsScreen,
      meta: {
        group: 'Assignments',
        pageClass: 'assignment-screen'
      }
    },
    {
      path: '/assignments/:assignmentId/students/:studentId',
      name: 'AssignmentStudentScreen',
      component: AssignmentsScreen,
      meta: {
        group: 'Assignments',
        pageClass: 'assignment-screen'
      }
    },
    {
      path: '/bundle/:bundleId/assignment/:assignmentId',
      name: 'BundleItemScreen',
      component: AssignmentsScreen,
      meta: {
        group: 'Assignments',
        pageClass: 'assignment-screen'
      }
    },
    {
      path: '/bundle/:bundleId/assignment/:assignmentId/student/:studentId',
      name: 'BundleStudentScreen',
      component: AssignmentsScreen,
      meta: {
        group: 'Assignments',
        pageClass: 'assignment-screen'
      }
    },
    {
      path: '/student/:studentId/assignments/:assignmentId',
      name: 'AssignmentsScreenForStudent',
      component: AssignmentsScreen,
      meta: {
        group: 'Assignments',
        pageClass: 'assignment-screen',
        isDisabledForSubstitute: true
      }
    },
    {
      path: '/student/:studentId/assignments/:assignmentId/students',
      name: 'AssignmentStudentScreenForStudent',
      component: AssignmentsScreen,
      meta: {
        group: 'Assignments',
        pageClass: 'assignment-screen',
        isDisabledForSubstitute: true
      }
    },
    {
      path: '/student/:studentId/bundle/:bundleId/assignment/:assignmentId',
      name: 'BundleStudentScreenForStudent',
      component: AssignmentsScreen,
      meta: {
        group: 'Assignments',
        pageClass: 'assignment-screen',
        isDisabledForSubstitute: true
      }
    },
    {
      path: '/purchase',
      name: 'PurchaseScreen',
      component: PurchaseScreen,
      alias: [
        '/purchase/enquiry',
        '/purchase/billing',
        '/purchase/payment',
        '/purchase/success',
        '/purchase/confirmed'
      ],
      meta: {
        group: 'PurchaseScreen'
      }
    },
    {
      path: '/dkt/sso',
      name: 'DKTAuthenticationScreen',
      component: DKTAuthenticationScreen,
      meta: {
        group: 'DKTAuthenticationScreen',
        options: {
          needHeader: false
        }
      }
    },
    {
      path: '/dkt/redirect',
      name: 'DKTRedirectScreen',
      component: DKTRedirectScreen,
      meta: {
        group: 'DKTRedirectScreen',
        options: {
          needHeader: false
        }
      }
    },
    {
      path: '/dkt-redirect',
      name: 'RedirectScreen',
      component: RedirectScreen,
      meta: {
        group: 'LandingScreen',
        options: {
          sidebar: false
        }
      }
    },
    {
      path: '/page-not-found',
      name: 'PageNotFoundScreen',
      component: PageNotFoundScreen,
      meta: {
        group: 'ErrorPages',
        options: {
          needHeader: false
        }
      }
    },
    {
      path: '/not-compatible',
      name: 'NotCompatibleScreen',
      component: NotCompatibleScreen,
      meta: {
        group: 'ErrorPages',
        options: {
          needHeader: false
        }
      }
    },
    {
      path: '/unsubscribed',
      name: 'UnsubscribedScreen',
      component: UnsubscribedScreen,
      meta: {
        group: 'LandingScreen',
        options: {
          needHeader: false
        }
      }
    },
    {
      path: '/privacy-policy',
      name: 'PrivacyPolicy',
      component: PrivacyPolicy,
      meta: {
        group: 'LandingScreen',
        options: {
          needHeader: false
        }
      }
    },
    {
      path: '/terms-and-conditions',
      name: 'TermsAndConditions',
      component: TermsAndConditions,
      meta: {
        group: 'LandingScreen',
        options: {
          needHeader: false
        }
      }
    },
    {
      // Public player for the listening PDF's QR code (no login: learners scan it).
      path: '/listen/:key',
      name: 'AiLessonListenScreen',
      component: AiLessonListenScreen,
      meta: {
        group: 'LandingScreen',
        hideAppBanner: true,
        options: {
          needHeader: false
        }
      }
    },
    {
      path: '/classes/:id/students/:studentId/summaryData',
      name: 'StudentSummaryData',
      component: StudentSummaryData,
      meta: {
        group: 'LandingScreen',
        pageClass: 'has-userimagegroup',
        pageTitleKey: 'app.classes.pageTitle',
        options: {
          needHeader: false
        },
        isDisabledForSubstitute: true
      }
    },
    {
      path: '/classes/:id/classSummaryData',
      name: 'ClassSummaryData',
      component: ClassSummaryData,
      meta: {
        group: 'LandingScreen',
        pageClass: 'has-userimagegroup',
        pageTitleKey: 'app.classes.pageTitle',
        options: {
          needHeader: false
        },
        isDisabledForSubstitute: true
      }
    },
    {
      path: '/users/coupons',
      name: 'Coupons',
      component: CouponsScreen,
      meta: {
        group: 'CouponManagement',
        options: {
          needHeader: true
        }
      }
    },
    {
      path: '/users/coupons/:id',
      name: 'CouponDetails',
      component: CouponScreen,
      meta: {
        group: 'CouponManagement',
        options: {
          needHeader: true
        }
      }
    },
    {
      path: '/users/accessibility-settings',
      name: 'AccessibilitySettingsScreen',
      component: AccessibilitySettingsScreen,
      meta: {
        group: 'Account',
        pageTitleKey:
          'app.header.userDropdownMenu.accessibilitySettingsLinkText'
      }
    }
  ]
});

router.beforeEach(guard);

export default router;



// WEBPACK FOOTER //
// ./src/router/index.js